import { createIntervalCalculator } from '@n2-common/machines/pollingMachine';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type Condition<TContext> = (context: TContext) => boolean;

/** General purpose hook, that fires refresh, if the refresh condition is met
 * @param refresh Function that is called to refresh context
 * @param context Data from refresh function call
 * @param retry Number of retries - defaults to 5
 * @param initialTimeout Number of milliseconds to wait before calling first refresh - defaults to 50
 * @param backoff Number of milliseconds to increment timeout before each retry - defaults to 500
 * @returns An object containing a refreshUntil function, which takes a predicate function,
 * to test against the context (witch should have been updated by the refresh call).
 */
export default function useRefresh<TContext>(refresh: (signal?: AbortSignal) => void, context?: TContext, retry = 5, initialTimeout = 50) {
    const refreshRef = useRef(refresh);
    const [condition, setCondition] = useState<Condition<TContext>>();
    const timeout = useRef(initialTimeout);
    const retries = useRef(0);
    const getTimeout = useMemo(() => createIntervalCalculator({ type: 'linear-backoff', retries: retry, interval: 2000 }), [retry]);
    const onConditionNotMetRef = useRef((reason: string) => console.log(reason));
    const onConditionMetRef = useRef<() => void>(() => undefined);

    const refreshUntil = useCallback(
        (refreshCondition: Condition<TContext>) => {
            timeout.current = initialTimeout;
            retries.current = 0;
            setCondition(() => refreshCondition);
            return new Promise<void>((resolve, reject) => {
                onConditionNotMetRef.current = reject;
                onConditionMetRef.current = resolve;
            });
        },
        [initialTimeout]
    );

    useEffect(() => {
        const controller = new AbortController();
        if (typeof condition == 'function' && context) {
            if (!condition(context)) {
                if (++retries.current <= retry) {
                    setTimeout(() => refreshRef.current(controller.signal), timeout.current);
                    timeout.current = getTimeout(retries.current);
                } else {
                    onConditionNotMetRef.current(`Refresh did not reach condition after ${retry} retries.`);
                }
            } else {
                timeout.current = initialTimeout;
                retries.current = 0;
                setCondition(undefined);
                onConditionMetRef.current();
            }
        }
        return () => controller.abort();
    }, [context, condition, retry, initialTimeout, getTimeout]);
    return { refreshUntil };
}
